/**
 * =============================================================================
 *  DONG HINH DANG (/AP) - LY DO CHU THICH NHIN THAY DUOC O TRINH XEM KHAC
 * =============================================================================
 *
 *  Mot muc /Annots hop le, dung /Subtype, dung /Rect, dung /C - va KHONG co /AP
 *  - la mot chu thich VO HINH trong rat nhieu trinh xem.
 *
 *  Dac ta cho phep trinh xem tu sinh hinh dang tu cac truong cua chu thich,
 *  nhung do la tuy chon chu khong bat buoc, va trong thuc te:
 *      - Acrobat sinh duoc, va tu ghi /AP vao tep khi ban luu.
 *      - pdf.js sinh duoc cho mot so loai.
 *      - Preview cua macOS, nhieu trinh xem di dong, va phan lon duong IN thi
 *        KHONG: khong co /AP thi khong co gi tren giay.
 *
 *  Do chinh la che do hong so 1 trong danh sach bai hoc, chi doi ao: mot tep
 *  hop le, page.getAnnotations() doc ra day du, ma tren trang thi trong tron.
 *  Nen o day MOI loai chu thich ta ghi ra deu co /AP. Khong co ngoai le nao.
 *
 * =============================================================================
 *  HE TOA DO: BBOX = RECT, MATRIX = DON VI
 * =============================================================================
 *  Dac ta noi hinh dang duoc bien doi sao cho hop bao cua no (sau /Matrix) khop
 *  vao /Rect. Neu ta dat /BBox DUNG BANG /Rect va /Matrix la ma tran don vi thi
 *  phep bien doi do la phep dong nhat, va noi dung dong hinh dang duoc ve trong
 *  CHINH he toa do cua trang.
 *
 *  Nho vay moi con so trong tep nay la toa do trang - khong co phep tru y, khong
 *  co goc toa do thu hai de nham lan. Day la lua chon co chu dich.
 *
 * =============================================================================
 *  DO MO DUOC AP DUNG DUNG MOT LAN
 * =============================================================================
 *  Do mo song o hai cho: khoa /CA cua chu thich, va toan tu ExtGState /ca ben
 *  trong dong hinh dang. Dat ca hai thi do mo bi NHAN HAI LAN (0.45 -> 0.20).
 *
 *  Quy uoc chot: /CA tren chu thich mang do mo; dong hinh dang luon ve DAC.
 *  Day cung la cach Acrobat ghi.
 */
import type { Annotation, PdfPoint, PdfRect } from '@pdfeditor/types';
import { DEFAULT_BORDER_WIDTH_PT } from './support';
import { bboxOf, endpointsOf, fillOf, strokeWidthOf } from './shapes';
import type { PlacedBoxLine } from '../../ui/textBoxMetrics';
import { DEFAULT_TEXT_COLOR_RGB, NOTE_FILL_RGB } from '../../ui/textBoxMetrics';

/** Ten XObject cua anh trong tai nguyen dong hinh dang. */
export const IMAGE_XOBJECT_NAME = 'Im0';

/** Ten font trong tai nguyen dong hinh dang. */
export const TEXT_FONT_NAME = 'F0';

export interface AppearanceStream {
  /** Noi dung dong hinh dang, toa do trang. */
  content: string;
  /** Hop bao. Bang /Rect cua chu thich - xem ghi chu dau tep. */
  bbox: PdfRect;
  /** Tai nguyen can cho dong hinh dang (co the rong). */
  resources: Record<string, unknown>;
}

export interface AppearanceOptions {
  /** Be rong net du phong khi chu thich khong mang gia tri rieng. */
  lineWidthPt?: number;
  /**
   * Tham chieu toi anh DA NHUNG, cho kind 'stamp'.
   *
   * Kieu `unknown` la co y: tep nay khong duoc biet gi ve pdf-lib. No dung hinh
   * hoc va sinh chuoi toan tu; viec nhung anh la viec cua write.ts, noi duy nhat
   * co PDFDocument trong tay. Tra ve mot dong hinh dang co ten /Im0 ma khong co
   * ai gan ref vao thi anh se KHONG hien - nen buildAppearance() tu choi dung
   * dong hinh dang cho 'stamp' khi thieu tham chieu, chu khong dung mot cai rong.
   */
  imageRef?: unknown;

  /**
   * CHU DA DUOC DO XONG, cho 'free-text' va cho ghi chu dang hop.
   *
   * Cung ly do nhu `imageRef`: tep nay khong biet gi ve pdf-lib va khong biet gi
   * ve fontkit. No nhan VI TRI da tinh san roi sinh toan tu.
   *
   * Va quan trong hon: `lines` phai la KET QUA cua chinh phep do ma ban xem
   * truoc tren man hinh da dung (ui/textBoxMetrics.ts). Neu cho nay tu ngat dong
   * lay thi ban xem truoc va tep xuat ra se troi khoi nhau - bai hoc so 3.
   */
  text?: {
    /**
     * Tham chieu toi MAT CHU DA NHUNG.
     *
     * DAM VA NGHIENG SONG O DAY, khong o mot khoa rieng nao. Chung la mat chu
     * THAT (NotoSans-Bold.ttf, -Italic.ttf, -BoldItalic.ttf), khong phai hieu
     * ung ve len ban Regular: khong co phep to vien de gia lam net day, khong
     * co ma tran truot de gia lam chu nghieng. Ca hai meo do deu day dau tieng
     * Viet ra khoi than chu - va tieng Viet chong hai tang dau thi khong chiu
     * duoc dieu do.
     *
     * Nen tep nay khong can biet gi ve "dam" ca: ben goi (write.ts) da chon
     * dung tep font va nhung no, roi trao vao day mot tham chieu. Cung tham
     * chieu do di vao /AP resources ben duoi, nen dong hinh dang khong the ve
     * bang mot mat chu khac voi mat chu da duoc do.
     */
    fontRef: unknown;
    /** Ma hoa chuoi theo bang ma cua font da nhung, ra dang `<...>` cua PDF. */
    encodeHex(text: string): string;
    /** Cac dong da dat, toa do TUONG DOI so voi goc duoi-trai cua `rect`. */
    lines: readonly PlacedBoxLine[];
    fontSizePt: number;
  };
}

/** So thuc dang toan tu PDF: bo so 0 thua, khong dung ky hieu mu. */
export function num(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 1000) / 1000;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

function rgbOps(color: readonly [number, number, number], stroke: boolean): string {
  const op = stroke ? 'RG' : 'rg';
  return `${num(color[0])} ${num(color[1])} ${num(color[2])} ${op}`;
}

/** Hop bao cua mot chum diem, noi rong them `pad`. */
export function boundsOfPoints(paths: readonly (readonly PdfPoint[])[], pad = 0): PdfRect {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const path of paths) {
    for (const point of path) {
      if (point.x < x0) x0 = point.x;
      if (point.y < y0) y0 = point.y;
      if (point.x > x1) x1 = point.x;
      if (point.y > y1) y1 = point.y;
    }
  }
  if (!Number.isFinite(x0)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: x0 - pad, y: y0 - pad, width: x1 - x0 + pad * 2, height: y1 - y0 + pad * 2 };
}

/**
 * TOAN TU KET THUC DUONG, chon theo viec co to ben trong hay khong.
 *
 * 'S' = chi ke vien. 'B' = to roi ke vien. Chon nham 'B' cho mot hinh khong to
 * se to bang mau ve dang co - va vi mau ve mac dinh la DEN, mot khung rong bong
 * nhien thanh mot o den dac che kin chu ben duoi. Do la ly do ham nay ton tai
 * thay vi mot chuoi viet thang o ba cho.
 */
function paintOp(hasFill: boolean): string {
  return hasFill ? 'B' : 'S';
}

/** GACH CHAN / GACH NGANG. Mot doan thang dac o duong cao thich hop. */
function lineMarkAppearance(annotation: Annotation, position: 'under' | 'through'): AppearanceStream {
  const r = annotation.rect;
  const width = Math.max(0.6, r.height * 0.07);
  // Gach chan nam hoi duoi duong co so; gach ngang nam giua than chu.
  const y = position === 'under' ? r.y + r.height * 0.06 : r.y + r.height * 0.45;
  return {
    content: [
      'q',
      rgbOps(annotation.colorRgb, true),
      `${num(width)} w`,
      `${num(r.x)} ${num(y)} m`,
      `${num(r.x + r.width)} ${num(y)} l`,
      'S',
      'Q',
    ].join('\n'),
    bbox: r,
    resources: {},
  };
}

/** GACH LUON SONG. Mot day cung nua vong, buoc theo chieu cao chu. */
function squigglyAppearance(annotation: Annotation): AppearanceStream {
  const r = annotation.rect;
  const step = Math.max(2, r.height * 0.28);
  const amplitude = step * 0.5;
  const baseY = r.y + r.height * 0.04;
  const ops: string[] = ['q', rgbOps(annotation.colorRgb, true), `${num(Math.max(0.5, step * 0.14))} w`];
  ops.push(`${num(r.x)} ${num(baseY)} m`);
  let up = true;
  for (let x = r.x; x < r.x + r.width; x += step) {
    const nextX = Math.min(x + step, r.x + r.width);
    const midX = (x + nextX) / 2;
    const peakY = baseY + (up ? amplitude : 0);
    ops.push(
      `${num(midX)} ${num(peakY)} ${num(midX)} ${num(peakY)} ${num(nextX)} ${num(baseY)} c`,
    );
    up = !up;
  }
  ops.push('S', 'Q');
  return { content: ops.join('\n'), bbox: r, resources: {} };
}

/** VE TAY. Duong gap khuc, dau tron va goi tron. */
function inkAppearance(annotation: Annotation, lineWidthPt: number): AppearanceStream {
  const paths = (annotation.inkPaths ?? []).filter((p) => p.length >= 2);
  const ops: string[] = [
    'q',
    rgbOps(annotation.colorRgb, true),
    `${num(lineWidthPt)} w`,
    '1 J', // dau tron
    '1 j', // goi tron
  ];
  for (const path of paths) {
    const first = path[0] as PdfPoint;
    ops.push(`${num(first.x)} ${num(first.y)} m`);
    for (let i = 1; i < path.length; i++) {
      const point = path[i] as PdfPoint;
      ops.push(`${num(point.x)} ${num(point.y)} l`);
    }
    ops.push('S');
  }
  ops.push('Q');
  return {
    content: ops.join('\n'),
    // Hop bao phai NOI RONG: net duoc ve tam duong tren duong di, nen mot nua
    // be day luon nam ngoai hop bao cua cac diem, va goi tron o mot goc nhon con
    // an ra xa hon nua. Khong noi rong thi /BBox cat cut chinh net vua ve.
    // Noi rong ca mot be rong net (thay vi nua) la co y rong rai - thua vai
    // phan muoi diem thi khong ai thay, con thieu thi net bi xen.
    bbox: boundsOfPoints(paths, lineWidthPt),
    resources: {},
  };
}

/** KHUNG CHU NHAT. Ve THUT VAO nua be rong net de net nam tron trong /Rect. */
function squareAppearance(annotation: Annotation, lineWidthPt: number): AppearanceStream {
  const r = bboxOf(annotation);
  const half = lineWidthPt / 2;
  const fill = fillOf(annotation);
  return {
    content: [
      'q',
      ...(fill ? [rgbOps(fill, false)] : []),
      rgbOps(annotation.colorRgb, true),
      `${num(lineWidthPt)} w`,
      `${num(r.x + half)} ${num(r.y + half)} ${num(Math.max(0, r.width - lineWidthPt))} ${num(
        Math.max(0, r.height - lineWidthPt),
      )} re`,
      paintOp(fill !== null),
      'Q',
    ].join('\n'),
    bbox: r,
    resources: {},
  };
}

/** KHUNG TRON. Bon cung Bezier - PDF khong co toan tu cung tron. */
function circleAppearance(annotation: Annotation, lineWidthPt: number): AppearanceStream {
  const r = bboxOf(annotation);
  const half = lineWidthPt / 2;
  const fill = fillOf(annotation);
  const rx = Math.max(0, r.width / 2 - half);
  const ry = Math.max(0, r.height / 2 - half);
  const cx = r.x + r.width / 2;
  const cy = r.y + r.height / 2;
  // 0.5523 = 4/3 * (sqrt(2) - 1): hang so xap xi mot phan tu duong tron bang
  // mot doan Bezier bac ba, sai so duoi 0.02%.
  const kx = rx * 0.5523;
  const ky = ry * 0.5523;
  return {
    content: [
      'q',
      ...(fill ? [rgbOps(fill, false)] : []),
      rgbOps(annotation.colorRgb, true),
      `${num(lineWidthPt)} w`,
      `${num(cx + rx)} ${num(cy)} m`,
      `${num(cx + rx)} ${num(cy + ky)} ${num(cx + kx)} ${num(cy + ry)} ${num(cx)} ${num(cy + ry)} c`,
      `${num(cx - kx)} ${num(cy + ry)} ${num(cx - rx)} ${num(cy + ky)} ${num(cx - rx)} ${num(cy)} c`,
      `${num(cx - rx)} ${num(cy - ky)} ${num(cx - kx)} ${num(cy - ry)} ${num(cx)} ${num(cy - ry)} c`,
      `${num(cx + kx)} ${num(cy - ry)} ${num(cx + rx)} ${num(cy - ky)} ${num(cx + rx)} ${num(cy)} c`,
      paintOp(fill !== null),
      'Q',
    ].join('\n'),
    bbox: r,
    resources: {},
  };
}

/**
 * Hai dau mut cua duong thang / mui ten.
 *
 * Xuat lai tu shapes.ts chu khong dinh nghia lai: hai ban sao cua cung phep tinh
 * la cach ban xem truoc bat dau lech khoi tep xuat ra.
 */
export function lineEndpoints(rect: PdfRect): { from: PdfPoint; to: PdfPoint } {
  return endpointsOf(rect);
}

/** DUONG THANG / MUI TEN. Mui ten them hai net cheo o dau `to`. */
function lineAppearance(
  annotation: Annotation,
  lineWidthPt: number,
  arrow: boolean,
): AppearanceStream {
  const r = annotation.rect;
  const { from, to } = lineEndpoints(r);
  const ops: string[] = [
    'q',
    rgbOps(annotation.colorRgb, true),
    `${num(lineWidthPt)} w`,
    '1 J',
    '1 j',
    `${num(from.x)} ${num(from.y)} m`,
    `${num(to.x)} ${num(to.y)} l`,
    'S',
  ];

  if (arrow) {
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const size = Math.max(6, lineWidthPt * 4);
    const spread = Math.PI / 7;
    const leftX = to.x - size * Math.cos(angle - spread);
    const leftY = to.y - size * Math.sin(angle - spread);
    const rightX = to.x - size * Math.cos(angle + spread);
    const rightY = to.y - size * Math.sin(angle + spread);
    ops.push(
      `${num(leftX)} ${num(leftY)} m`,
      `${num(to.x)} ${num(to.y)} l`,
      `${num(rightX)} ${num(rightY)} l`,
      'S',
    );
  }
  ops.push('Q');

  // Mui ten tran ra ngoai hai dau mut, va /BBox cat phang moi thu nam ngoai no.
  // Hop bao lay qua bboxOf(): `r` co the co be rong / chieu cao AM (duong keo
  // sang trai hoac xuong duoi), va padRect() tren mot hop am cho ra mot /BBox
  // lat nguoc - dung nghia la mot mui ten vo hinh.
  return {
    content: ops.join('\n'),
    bbox: padRect(bboxOf(annotation), Math.max(6, lineWidthPt * 4)),
    resources: {},
  };
}

/**
 * ANH CHEN VAO TRANG (/Stamp).
 *
 * Dong hinh dang chi la mot phep bien doi he toa do roi ve XObject anh: `cm`
 * dua hinh vuong don vi (0,0)-(1,1) cua anh vao dung o chu nhat cua chu thich.
 * Do la cach duy nhat - XObject anh LUON co he toa do rieng la hinh vuong don
 * vi, bat ke anh bao nhieu diem anh.
 */
function stampAppearance(annotation: Annotation, imageRef: unknown): AppearanceStream {
  const r = bboxOf(annotation);
  return {
    content: [
      'q',
      `${num(r.width)} 0 0 ${num(r.height)} ${num(r.x)} ${num(r.y)} cm`,
      `/${IMAGE_XOBJECT_NAME} Do`,
      'Q',
    ].join('\n'),
    bbox: r,
    resources: { XObject: { [IMAGE_XOBJECT_NAME]: imageRef } },
  };
}

/**
 * GHI CHU DINH. Mot bieu tuong bong thoai co vien.
 *
 * Nhieu trinh xem co bieu tuong rieng cho /Text va se dung bieu tuong do thay
 * cho /AP. Ta van ghi /AP vi nhung trinh xem KHONG co thi chi con /AP - va
 * khong co /AP nghia la ghi chu bien mat khoi ban in.
 */
function noteAppearance(annotation: Annotation): AppearanceStream {
  const r = annotation.rect;
  const w = r.width;
  const h = r.height;
  const x = r.x;
  const y = r.y;
  const radius = Math.min(w, h) * 0.18;
  const bodyBottom = y + h * 0.3;
  return {
    content: [
      'q',
      rgbOps(annotation.colorRgb, false),
      '0 0 0 RG',
      `${num(Math.max(0.4, Math.min(w, h) * 0.05))} w`,
      // Than bong thoai, bon goc bo tron.
      `${num(x + radius)} ${num(bodyBottom)} m`,
      `${num(x)} ${num(bodyBottom)} ${num(x)} ${num(bodyBottom + radius)} ${num(x)} ${num(bodyBottom + radius)} c`,
      `${num(x)} ${num(y + h - radius)} l`,
      `${num(x)} ${num(y + h)} ${num(x + radius)} ${num(y + h)} ${num(x + radius)} ${num(y + h)} c`,
      `${num(x + w - radius)} ${num(y + h)} l`,
      `${num(x + w)} ${num(y + h)} ${num(x + w)} ${num(y + h - radius)} ${num(x + w)} ${num(y + h - radius)} c`,
      `${num(x + w)} ${num(bodyBottom + radius)} l`,
      `${num(x + w)} ${num(bodyBottom)} ${num(x + w - radius)} ${num(bodyBottom)} ${num(x + w - radius)} ${num(bodyBottom)} c`,
      'h',
      // Duoi bong thoai.
      `${num(x + w * 0.3)} ${num(bodyBottom)} m`,
      `${num(x + w * 0.26)} ${num(y)} l`,
      `${num(x + w * 0.52)} ${num(bodyBottom)} l`,
      'h',
      'B',
      // Ba dong ke gia y hieu "co chu ben trong".
      '0 0 0 RG',
      `${num(Math.max(0.35, Math.min(w, h) * 0.04))} w`,
      `${num(x + w * 0.2)} ${num(y + h * 0.78)} m`,
      `${num(x + w * 0.8)} ${num(y + h * 0.78)} l`,
      `${num(x + w * 0.2)} ${num(y + h * 0.62)} m`,
      `${num(x + w * 0.8)} ${num(y + h * 0.62)} l`,
      `${num(x + w * 0.2)} ${num(y + h * 0.46)} m`,
      `${num(x + w * 0.6)} ${num(y + h * 0.46)} l`,
      'S',
      'Q',
    ].join('\n'),
    bbox: r,
    resources: {},
  };
}

/**
 * VE CAC DONG CHU vao mot hop, dung vi tri da do san.
 *
 * `lines` mang toa do TUONG DOI so voi goc duoi-trai cua `rect`, y huong len -
 * dung he cua PDF, nen cong thang vao la xong, khong co phep lat truc nao o day.
 */
function textOps(
  rect: PdfRect,
  text: NonNullable<AppearanceOptions['text']>,
  colorRgb: readonly [number, number, number],
): string[] {
  const ops: string[] = ['BT', rgbOps(colorRgb, false), `/${TEXT_FONT_NAME} ${num(text.fontSizePt)} Tf`];
  for (const line of text.lines) {
    if (line.text.length === 0) continue;
    // Td la phep doi TUYET DOI so voi goc dong chu, nen dat lai truoc moi dong
    // thay vi cong don: cong don thi mot dong rong se lam lech tat ca dong sau.
    ops.push('1 0 0 1 0 0 Tm');
    ops.push(`${num(rect.x + line.xPt)} ${num(rect.y + line.baselineYPt)} Td`);
    ops.push(`${text.encodeHex(line.text)} Tj`);
  }
  ops.push('ET');
  return ops;
}

/** Hinh chu nhat bo goc, dung lam than cua hop chu va cua bong thoai. */
function roundedRectOps(r: PdfRect, radius: number): string[] {
  const k = Math.min(radius, r.width / 2, r.height / 2);
  const x1 = r.x + r.width;
  const y1 = r.y + r.height;
  return [
    `${num(r.x + k)} ${num(r.y)} m`,
    `${num(x1 - k)} ${num(r.y)} l`,
    `${num(x1)} ${num(r.y)} ${num(x1)} ${num(r.y + k)} ${num(x1)} ${num(r.y + k)} c`,
    `${num(x1)} ${num(y1 - k)} l`,
    `${num(x1)} ${num(y1)} ${num(x1 - k)} ${num(y1)} ${num(x1 - k)} ${num(y1)} c`,
    `${num(r.x + k)} ${num(y1)} l`,
    `${num(r.x)} ${num(y1)} ${num(r.x)} ${num(y1 - k)} ${num(r.x)} ${num(y1 - k)} c`,
    `${num(r.x)} ${num(r.y + k)} l`,
    `${num(r.x)} ${num(r.y)} ${num(r.x + k)} ${num(r.y)} ${num(r.x + k)} ${num(r.y)} c`,
    'h',
  ];
}

/**
 * HOP CHU DOC DUOC - dung cho 'free-text' va cho ghi chu dang 'callout'.
 *
 * Day la cau tra loi cho dieu nguoi dung phan nan: mot ghi chu phai la chu DOC
 * DUOC tren trang, khong phai mot bieu tuong vuong nho ma noi dung chi hien ra
 * khi ro chuot. Bieu tuong van con, nhung la mot lua chon (`noteStyle: 'icon'`),
 * khong con la duy nhat.
 *
 * Khong co `text` thi tra ve null chu KHONG ve mot hop rong: mot hop mau khong
 * chu la dung che do hong "tep hop le, tren giay khong co gi" - giong het ly do
 * 'stamp' tu choi khi thieu tham chieu anh.
 */
function textBoxAppearance(
  annotation: Annotation,
  options: AppearanceOptions,
  lineWidthPt: number,
): AppearanceStream | null {
  const text = options.text;
  if (!text) return null;

  const r = annotation.rect;
  // `fillRgb` phan biet ba trang thai: bo qua = mau mac dinh cua loai, null =
  // KHONG to (thay duoc chu ben duoi), co gia tri = to mau do.
  const fill =
    annotation.fillRgb === null
      ? null
      : (annotation.fillRgb ?? (annotation.kind === 'text-note' ? NOTE_FILL_RGB : null));
  const stroke = annotation.colorRgb;
  const textColor = annotation.textColorRgb ?? DEFAULT_TEXT_COLOR_RGB;

  /*
   * =========================================================================
   *  HOP CHU TU DO KHONG CO VIEN
   * =========================================================================
   *
   *  Yeu cau cua nguoi dung, nguyen van: "chèn hộp chữ thì bỏ border đi bạn,
   *  chỉ cần hiện text thôi". Mot hop chu dat len tren mot trang co san phai
   *  nhin nhu chu duoc go THEM VAO trang, khong nhu mot cai nhan dan de len.
   *
   *  CHI 'free-text'. Ghi chu dang 'callout' GIU NGUYEN giay vang co vien: no
   *  la mot loi binh ben le, va vien chinh la thu noi rang no khong thuoc noi
   *  dung goc. Nguoi dung xin bo vien cua hop chu, khong xin bo vien cua ghi chu.
   *
   *  NEN: hop chu tu do mac dinh TRONG SUOT HOAN TOAN (`fillRgb` vang mat cho
   *  ra null o tren), nen khi khong to gi thi doan nay khong sinh MOT toan tu ve
   *  hinh nao - dong hinh dang chi con glyph. To nen van con lam duoc neu nguoi
   *  dung chon ro mot mau trong bang thuoc tinh; thu bi bo la VIEN, khong phai
   *  kha nang to nen.
   *
   *  `colorRgb` cua 'free-text' tu day khong ve gi ca - giong /Stamp. No van
   *  duoc ghi vao khoa /C de tu dien chu thich hop le va de vong "ghi ra doc
   *  lai" con doi chieu duoc.
   *
   *  Ban tren man hinh (viewer/AnnotationLayer.tsx, TextBoxGraphic) ap DUNG quy
   *  tac nay. Sua mot ben ma quen ben kia la bai hoc so 3 - ban xem truoc noi doi.
   */
  const bordered = annotation.kind !== 'free-text';

  const ops: string[] = ['q'];
  const body = roundedRectOps(r, Math.min(4, r.height / 2));
  if (bordered) {
    if (fill) {
      ops.push(rgbOps(fill, false));
    }
    ops.push(rgbOps(stroke, true), `${num(lineWidthPt)} w`, ...body, fill ? 'B' : 'S');
  } else if (fill) {
    // To ma khong ke vien: 'f', khong phai 'B'. 'B' se ke vien bang mau ve dang
    // co - dung cai vua bo di, chi la bang mot mau khac.
    ops.push(rgbOps(fill, false), ...body, 'f');
  }
  ops.push(...textOps(r, text, textColor));
  ops.push('Q');

  return { content: ops.join('\n'), bbox: r, resources: { Font: { [TEXT_FONT_NAME]: text.fontRef } } };
}

export function padRect(rect: PdfRect, pad: number): PdfRect {
  return {
    x: rect.x - pad,
    y: rect.y - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}

/**
 * Dong hinh dang cho mot chu thich.
 *
 * Tra ve null CHI khi loai chu thich khong nam trong danh sach ghi duoc - va
 * ben goi (write.ts) da chan truoc bang checkAnnotation(), nen tren thuc te
 * duong nay khong chay. Neu no chay thi day la loi lap trinh, khong phai dau
 * vao xau, va ben goi phai bao loi chu khong duoc ghi mot chu thich khong /AP.
 */
export function buildAppearance(
  annotation: Annotation,
  options: AppearanceOptions | number = {},
): AppearanceStream | null {
  // Nap chong so hoc: ban goi cu truyen thang mot be rong net. Giu lai de moi
  // cho goi cu van dung, va vi mot tham so tuy chon co hai hinh dang thi ro
  // hon la hai ham gan giong nhau.
  const opts: AppearanceOptions = typeof options === 'number' ? { lineWidthPt: options } : options;
  // Be rong net CUA TUNG DOI TUONG thang tham so chung. Xem strokeWidthOf().
  const lineWidthPt = strokeWidthOf(annotation, opts.lineWidthPt ?? DEFAULT_BORDER_WIDTH_PT);

  switch (annotation.kind) {
    case 'underline':
      return lineMarkAppearance(annotation, 'under');
    case 'strikeout':
      return lineMarkAppearance(annotation, 'through');
    case 'squiggly':
      return squigglyAppearance(annotation);
    case 'ink':
      return inkAppearance(annotation, lineWidthPt);
    case 'square':
      return squareAppearance(annotation, lineWidthPt);
    case 'circle':
      return circleAppearance(annotation, lineWidthPt);
    case 'line':
      return lineAppearance(annotation, lineWidthPt, false);
    case 'arrow':
      return lineAppearance(annotation, lineWidthPt, true);
    case 'text-note':
      // MAC DINH LA HOP DOC DUOC. Bieu tuong nho chi khi duoc yeu cau ro rang -
      // dao nguoc so voi truoc day, va do chinh la thay doi nguoi dung xin.
      return annotation.noteStyle === 'icon'
        ? noteAppearance(annotation)
        : textBoxAppearance(annotation, opts, lineWidthPt);
    case 'free-text':
      return textBoxAppearance(annotation, opts, lineWidthPt);
    case 'stamp':
      // Khong co tham chieu anh thi KHONG dung dong hinh dang. Mot /AP goi
      // /Im0 ma tai nguyen khong co /Im0 la mot o trong - dung che do hong
      // "tep hop le, tren giay khong co gi" ma ca tep nay ton tai de chan.
      return opts.imageRef === undefined || opts.imageRef === null
        ? null
        : stampAppearance(annotation, opts.imageRef);
    default:
      return null;
  }
}
